/**
 * Client mirror of the frozen weighted-criteria contract.
 *
 * The normative source is `app/Enum/JobCriterionKey.php` and
 * `app/Enum/JobCriterionBucket.php`; `docs/architecture/job-board/criteria-contract.md`
 * carries the value vocabularies and the legacy flat-filter adapter map.
 *
 * Changing any of this is a contract change — update the enums, the doc and this
 * file together. `tests/Feature/JobBoard/CriteriaContractParityTest.php` fails if
 * the two sides drift apart.
 */

export const CRITERIA_VERSION = 1;

export const CRITERION_BUCKET = {
	HARD: 'hard',
	RANKED: 'ranked',
};

/**
 * Why the relaxation ladder stopped — `App\Enum\JobRelaxationStop`.
 *
 * `FLOOR_MET` is the only case that means the seeker is looking at a full result set;
 * every other one is an honest low count. `PINNED` never reaches the banner, because a
 * paged response is not allowed to recompute the one page 1 resolved.
 */
export const RELAXATION_STOP = {
	FLOOR_MET: 'floor_met',
	EXHAUSTED: 'exhausted',
	STEP_CAPPED: 'step_capped',
	PINNED: 'pinned',
};

// Declaration order matches JobCriterionKey's cases.
export const CRITERION_KEY = {
	TITLE: 'title',
	FRESHNESS: 'freshness',
	SALARY: 'salary',
	LOCATION: 'location',
	WORK_SETTING: 'work_setting',
	INDUSTRY: 'industry',
	YOE: 'yoe',
	VISA: 'visa',
	AUTO_APPLY: 'auto_apply',
};

/**
 * The default ranked stack, highest priority first — JobCriterionKey::defaultRankOrder().
 *
 * Deliberately not the declaration order (location outranks salary), and hard-only
 * keys are absent by construction. Doubles as the anonymous default algorithm.
 */
export const DEFAULT_RANK_ORDER = [
	CRITERION_KEY.FRESHNESS,
	CRITERION_KEY.TITLE,
	CRITERION_KEY.LOCATION,
	CRITERION_KEY.SALARY,
	CRITERION_KEY.WORK_SETTING,
	CRITERION_KEY.INDUSTRY,
	CRITERION_KEY.YOE,
	CRITERION_KEY.AUTO_APPLY,
];

/** Hard-only by nature: work authorisation is binary, so partial credit is meaningless. */
export const HARD_ONLY_KEYS = [CRITERION_KEY.VISA];

/**
 * Keys with no data source until the enrichment pipeline lands (EN-818/EN-822).
 * They render as inactive rows and are omitted from the emitted payload.
 */
export const INACTIVE_KEYS = [CRITERION_KEY.VISA];

/** Row order for the full list: hard-only rows pinned first, then the default stack. */
export const DEFAULT_CRITERIA_ORDER = [...HARD_ONLY_KEYS, ...DEFAULT_RANK_ORDER];

/**
 * Seeker-facing labels, wording from Figma.
 *
 * `yoe` is one row ("Experience") where Figma drew two — the contract has a single
 * key and ranks must be contiguous, so Minimum/Maximum Experience cannot each hold
 * a rank. `auto_apply` has no Figma row and reuses the filter bar's existing label.
 */
export const CRITERION_LABELS = {
	[CRITERION_KEY.FRESHNESS]: 'Timing',
	[CRITERION_KEY.TITLE]: 'Target Roles',
	[CRITERION_KEY.LOCATION]: 'Location',
	[CRITERION_KEY.SALARY]: 'Salary',
	[CRITERION_KEY.WORK_SETTING]: 'Work Settings',
	[CRITERION_KEY.INDUSTRY]: 'Industry',
	[CRITERION_KEY.YOE]: 'Experience',
	[CRITERION_KEY.AUTO_APPLY]: 'AutoApply',
	[CRITERION_KEY.VISA]: 'Work Authorization',
};
